// =====================================================================
// src/App.tsx — Top-level app shell + auth gate
// =====================================================================

import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import { getSupabase } from '@/lib/db/client';
import type { Member } from '@/lib/db/types';
import { maybePromptAndMigrate } from '@/lib/migration/import-from-localstorage';
import { isMidInviteOnboarding, shouldEnterMainApp, shouldRouteToOnboarding } from '@/lib/invite-routing';
import {
  clearAllInviteState,
  getStoredInviteToken,
  isSignupEntryPath,
  resolveInviteToken,
} from '@/lib/invite-token';
import { isStandalonePwa } from '@/lib/pwa';
import { logAuthBootstrap } from '@/lib/db/auth-session';
import { SignInScreen } from '@/components/SignInScreen';
import { InviteOnlyGate } from '@/components/InviteOnlyGate';
import { OnboardingFlow } from '@/components/OnboardingFlow';
import { MainApp } from '@/components/MainApp';

type AppState =
  | { phase: 'loading' }
  | { phase: 'invite'; hasSession: boolean }
  | { phase: 'signin' }
  | { phase: 'onboarding'; token: string }
  | { phase: 'ready'; member: Member }
  | { phase: 'error'; message: string };

async function getSessionWithRetry(maxWaitMs = 3000) {
  const sb = getSupabase();
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const { data: { session } } = await sb.auth.getSession();
    if (session) return session;
    await new Promise((r) => window.setTimeout(r, 150));
  }
  return null;
}

function resolveOnboardingToken(): string | null {
  return resolveInviteToken() ?? (isSignupEntryPath() ? getStoredInviteToken() : null);
}

async function resolveInitialState(): Promise<Exclude<AppState, { phase: 'loading' }>> {
  const token = resolveOnboardingToken();
  const session = await getSessionWithRetry();
  void logAuthBootstrap();

  if (session) {
    let member = await db.auth.me();
    if (!member) {
      try {
        member = await db.auth.refresh();
      } catch {
        member = await db.auth.me();
      }
    }
    const resumeToken = resolveOnboardingToken();
    if (member && await shouldEnterMainApp(member, resumeToken)) {
      clearAllInviteState();
      return { phase: 'ready', member };
    }
    if (member && resumeToken && await shouldRouteToOnboarding(resumeToken)) {
      return { phase: 'onboarding', token: resumeToken };
    }

    if (resumeToken || isMidInviteOnboarding(true, false, resumeToken)) {
      const t = resumeToken ?? getStoredInviteToken();
      if (t) return { phase: 'onboarding', token: t };
    }
    return { phase: 'signin' };
  }

  if (token) {
    if (isStandalonePwa() || !(await shouldRouteToOnboarding(token))) {
      clearAllInviteState();
      return { phase: 'signin' };
    }
    return { phase: 'onboarding', token };
  }

  if (isStandalonePwa()) {
    return { phase: 'signin' };
  }

  return { phase: 'invite', hasSession: false };
}

export default function App() {
  const [state, setState] = useState<AppState>({ phase: 'loading' });
  const [slowLoad, setSlowLoad] = useState(false);

  useEffect(() => {
    let active = true;

    async function applyMember(member: Member | null) {
      if (!active) return;
      const resumeToken = resolveOnboardingToken();
      if (member && await shouldEnterMainApp(member, resumeToken)) {
        clearAllInviteState();
        await maybePromptAndMigrate();
        if (!active) return;
        setState({ phase: 'ready', member });
        return;
      }
      if (member && resumeToken && await shouldRouteToOnboarding(resumeToken)) {
        setState({ phase: 'onboarding', token: resumeToken });
        return;
      }

      const session = await getSessionWithRetry(1200);
      const token = resumeToken ?? resolveOnboardingToken();

      if (isMidInviteOnboarding(Boolean(session), false, token)) {
        const resumeToken = token ?? getStoredInviteToken();
        if (resumeToken) {
          setState({ phase: 'onboarding', token: resumeToken });
        }
        return;
      }

      if (token) {
        if (isStandalonePwa() || !(await shouldRouteToOnboarding(token))) {
          clearAllInviteState();
          setState({ phase: 'signin' });
          return;
        }
        setState({ phase: 'onboarding', token });
        return;
      }

      if (session) {
        setState({ phase: 'signin' });
        return;
      }

      if (isStandalonePwa()) {
        setState({ phase: 'signin' });
        return;
      }

      setState({ phase: 'invite', hasSession: false });
    }

    async function check() {
      try {
        const next = await resolveInitialState();
        if (!active) return;
        if (next.phase === 'ready') {
          clearAllInviteState();
          await maybePromptAndMigrate();
          if (!active) return;
        }
        setState(next);
      } catch (e) {
        if (active) setState({ phase: 'error', message: (e as Error).message });
      }
    }

    const slowTimer = window.setTimeout(() => {
      if (active) setSlowLoad(true);
    }, 8000);

    check();

    const unsubscribe = db.auth.onChange((m) => {
      if (!active) return;
      void applyMember(m);
    });

    return () => {
      active = false;
      window.clearTimeout(slowTimer);
      unsubscribe();
    };
  }, []);

  switch (state.phase) {
    case 'loading':
      return <LoadingScreen slow={slowLoad} onRetry={() => window.location.reload()} />;

    case 'invite':
      return (
        <InviteOnlyGate
          hasSession={state.hasSession}
          onSignIn={() => setState({ phase: 'signin' })}
        />
      );

    case 'signin':
      return (
        <SignInScreen
          onBack={isStandalonePwa() ? undefined : () => setState({ phase: 'invite', hasSession: false })}
        />
      );

    case 'onboarding':
      return (
        <OnboardingFlow
          token={state.token}
          onComplete={async () => {
            const member = await db.auth.refresh();
            if (member) setState({ phase: 'ready', member });
          }}
        />
      );

    case 'ready':
      return <MainApp member={state.member} />;

    case 'error':
      return <ErrorScreen message={state.message} />;
  }
}

function LoadingScreen({ slow, onRetry }: { slow?: boolean; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-center h-screen text-navy">
      <div className="text-center">
        <div className="text-3xl font-bold tracking-widest mb-2">BAG TAG</div>
        <div className="text-sm text-gray-500">{slow ? 'Still connecting…' : 'Loading…'}</div>
        {slow && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-lg bg-gold px-6 py-2 text-sm font-bold text-navy tracking-wider"
          >
            RETRY
          </button>
        )}
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-screen p-6 text-navy">
      <div className="max-w-sm text-center">
        <div className="text-2xl font-bold tracking-widest mb-4">BAG TAG</div>
        <div className="text-red-700 font-semibold mb-2">Something went wrong</div>
        <div className="text-sm text-gray-600 mb-4">{message}</div>
        <button
          onClick={() => window.location.reload()}
          className="bg-gold text-navy font-bold py-3 px-6 rounded-lg tracking-wider"
        >
          RELOAD
        </button>
      </div>
    </div>
  );
}
